import React, { useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, FormGroup, Label, Input } from "reactstrap";
import { sendTwilioMessage } from "../../api/TwilioApi";
import { sendMailgunEmail } from "../../api/DealerIntegrationApi";
import { failAlert, saveSuccessAlert } from "../../utils/alertUtils";
import { showApiError } from "../../utils/errorRoutingUtils";
import InputElement, {
	SelectorElement,
	PhoneInputElement,
} from "../../features/forms/components/InputElement";
import { FormProvider, useForm } from "react-hook-form";
import { generateRegistrationLink } from "../../utils/encryption";
import { formLoading } from "../../api/FormsAPI";
import swal from "sweetalert2";
import { MessageTemplateVariablesAndInput } from "../../features/communication/MessageTemplateVariables";
import { TemplateVariableContext } from "../../context/TemplateVariableContext";
import { MESSAGE_ACCOUNT_VARIABLES } from "../../constants/Constants";

const SendRegistrationLink = ({
	sendToNumber,
	customerEmail,
	accountID,
	toggle,
}) => {
	const user = JSON.parse(sessionStorage.getItem("user"));
	const dealerNumber = user?.messagingNumber;
	const dealerEmail = user?.locations?.email;

	const [toNumber, setToNumber] = useState(sendToNumber || "");
	const [toEmail, setToEmail] = useState(customerEmail || "");
	const [messageType, setMessageType] = useState(2); // 0=SMS, 1=Email, 2=Both
	const [subject, setSubject] = useState("Your Account Registration Link");
	const [registrationLink, setRegistrationLink] = useState("");
	const [isGenerating, setIsGenerating] = useState(true);
	const [isSending, setIsSending] = useState(false);

	// Template variables context
	const { setTemplateVars } = useContext(TemplateVariableContext);

	// Default message with template variables
	const defaultMessage = `Hi <buyer_firstName>,

Welcome to <dealer_companyName>!

Please click the link below to create your account for online payments:

{REGISTRATION_LINK}

If you have any questions, please contact us at <location_phone>.

Thank you,
<dealer_companyName>`;

	const [messageBody, setMessageBody] = useState(defaultMessage);

	const methods = useForm({
		reValidateMode: "onBlur",
		defaultValues: {
			messageBody: defaultMessage,
		},
	});

	// Initialize template variables for Account zone
	useEffect(() => {
		setTemplateVars(MESSAGE_ACCOUNT_VARIABLES);
	}, [setTemplateVars]);

	// Generate registration link on mount
	useEffect(() => {
		const generateLink = async () => {
			try {
				setIsGenerating(true);
				const link = await generateRegistrationLink(accountID);
				setRegistrationLink(link);
			} catch (error) {
				console.error("Error generating registration link:", error);
				failAlert("Failed to generate registration link");
			} finally {
				setIsGenerating(false);
			}
		};

		if (accountID) {
			generateLink();
		}
	}, [accountID]);

	const isToNumberValid = () => {
		if (!toNumber) return false;
		const numStr = String(toNumber);
		if (numStr.length !== 10) {
			failAlert("Phone number must be 10 digits");
			return false;
		}
		return true;
	};

	const isEmailValid = () => {
		if (!toEmail) return false;
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(toEmail)) {
			failAlert("Please enter a valid email address");
			return false;
		}
		return true;
	};

	const getMessageWithLink = () => {
		return messageBody.replace("{REGISTRATION_LINK}", registrationLink);
	};

	const sendSms = async () => {
		if (!isToNumberValid()) {
			return false;
		}

		const message = {
			fromNumber: "+1" + dealerNumber,
			recipients: ["+1" + toNumber],
			messageBody: getMessageWithLink(),
			personType: "Account",
			accountID,
			locationID: user?.locationID,
			messageType: 0,
		};

		try {
			await sendTwilioMessage(message);
			return true;
		} catch (err) {
			if (!err.isGeneralError) {
				showApiError(err, failAlert);
			}
			return false;
		}
	};

	const sendEmail = async () => {
		if (!isEmailValid()) {
			return false;
		}

		const email = {
			to: toEmail,
			from: dealerEmail,
			body: getMessageWithLink(),
			subject,
			accountID,
			personType: "Account",
			messageType: 1,
			locationID: user?.locationID,
		};

		try {
			await sendMailgunEmail(email);
			return true;
		} catch (err) {
			if (!err.isGeneralError) {
				showApiError(err, failAlert);
			}
			return false;
		}
	};

	const handleSend = async () => {
		if (!registrationLink) {
			failAlert("Registration link not generated yet. Please wait.");
			return;
		}

		setIsSending(true);
		formLoading("Sending");

		let smsSuccess = true;
		let emailSuccess = true;

		try {
			if (messageType === 0 || messageType === 2) {
				smsSuccess = await sendSms();
			}

			if (messageType === 1 || messageType === 2) {
				emailSuccess = await sendEmail();
			}

			swal.close();

			if (smsSuccess && emailSuccess) {
				saveSuccessAlert("Registration link sent successfully!");
				toggle();
			} else if (messageType === 2) {
				if (smsSuccess && !emailSuccess) {
					saveSuccessAlert("SMS sent, but email failed");
				} else if (!smsSuccess && emailSuccess) {
					saveSuccessAlert("Email sent, but SMS failed");
				}
			}
		} catch (error) {
			swal.close();
			failAlert("Failed to send registration link");
		} finally {
			setIsSending(false);
		}
	};

	if (isGenerating) {
		return (
			<Card>
				<CardBody className="text-center">
					<div className="spinner-border text-primary" role="status">
						<span className="sr-only">Generating link...</span>
					</div>
					<p className="mt-2">Generating registration link...</p>
				</CardBody>
			</Card>
		);
	}

	return (
		<FormProvider {...methods}>
			<Card>
				<CardBody>
					{/* Message Type Selector */}
					<SelectorElement
						{...methods}
						colSize="col-sm-4"
						value={messageType}
						name="messageType"
						label="Send Via"
						onChange={(e) => setMessageType(parseInt(e.target.value))}
						options={[
							{ value: 0, label: "SMS Only" },
							{ value: 1, label: "Email Only" },
							{ value: 2, label: "SMS & Email" },
						]}
					/>

					{/* Phone/Email Fields */}
					<div className="row">
						{(messageType === 0 || messageType === 2) && (
							<>
								<PhoneInputElement
									{...methods}
									colSize="col-lg-6"
									value={dealerNumber}
									name="twilioFromNumber"
									label="From Number"
									readOnly
								/>
								<PhoneInputElement
									{...methods}
									colSize="col-lg-6"
									value={toNumber}
									onChange={setToNumber}
									name="twilioToNumber"
									label="Customer Phone"
								/>
							</>
						)}
						{(messageType === 1 || messageType === 2) && (
							<>
								<InputElement
									{...methods}
									colSize="col-lg-6"
									value={dealerEmail}
									name="mailgunFromEmail"
									label="From Email"
									readOnly
								/>
								<InputElement
									{...methods}
									colSize="col-lg-6"
									value={toEmail}
									onChange={(e) => setToEmail(e.target.value)}
									name="mailgunToEmail"
									label="Customer Email"
								/>
							</>
						)}
					</div>

					{/* Email Subject */}
					{(messageType === 1 || messageType === 2) && (
						<div className="row">
							<InputElement
								{...methods}
								colSize="col-lg-12"
								value={subject}
								onChange={(e) => setSubject(e.target.value)}
								name="emailSubject"
								label="Subject"
							/>
						</div>
					)}

					{/* Message Body with Template Variables */}
					<div className="mt-3">
						<label className="text-muted small">
							Use {"{REGISTRATION_LINK}"} where you want the link to appear.
							Click on variables below to insert them.
						</label>
						<MessageTemplateVariablesAndInput
							body={messageBody}
							updateBody={setMessageBody}
						/>
					</div>

					{/* Action Buttons */}
					<div className="col-lg-12 d-flex justify-content-center mt-3">
						<Button
							color="primary"
							onClick={handleSend}
							disabled={isSending || !registrationLink}
							type="button"
						>
							{isSending ? "Sending..." : "Send Registration Link"}
						</Button>
						<Button
							color="secondary"
							onClick={toggle}
							className="ml-2"
							type="button"
						>
							Cancel
						</Button>
					</div>
				</CardBody>
			</Card>
		</FormProvider>
	);
};

export default SendRegistrationLink;
